// src/theme.js
import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1e1e1e',
      light: '#484848',
      dark: '#000000',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f5f5f5',
      light: '#ffffff',
      dark: '#c2c2c2',
      contrastText: '#000000',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e1e1e',
      secondary: '#555555',
    },
    divider: 'rgba(0, 0, 0, 0.08)',
    error: {
      main: '#d32f2f',
      light: 'rgba(211, 47, 47, 0.15)',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '1.75rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 500,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 500,
    },
    h5: {
      fontSize: '1.1rem',
      fontWeight: 500,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 400,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0, 0, 0, 0.05)',
    '0px 4px 6px rgba(0, 0, 0, 0.07)',
    '0px 6px 12px rgba(0, 0, 0, 0.08)',
    '0px 8px 16px rgba(0, 0, 0, 0.09)',
    '0px 10px 20px rgba(0, 0, 0, 0.1)',
    '0px 12px 24px rgba(0, 0, 0, 0.11)',
    '0px 14px 28px rgba(0, 0, 0, 0.12)',
    '0px 16px 32px rgba(0, 0, 0, 0.13)',
    '0px 18px 36px rgba(0, 0, 0, 0.14)',
    '0px 20px 40px rgba(0, 0, 0, 0.15)',
    '0px 22px 44px rgba(0, 0, 0, 0.16)',
    '0px 24px 48px rgba(0, 0, 0, 0.17)',
    '0px 26px 52px rgba(0, 0, 0, 0.18)',
    '0px 28px 56px rgba(0, 0, 0, 0.19)',
    '0px 30px 60px rgba(0, 0, 0, 0.2)',
    '0px 32px 64px rgba(0, 0, 0, 0.21)',
    '0px 34px 68px rgba(0, 0, 0, 0.22)',
    '0px 36px 72px rgba(0, 0, 0, 0.23)',
    '0px 38px 76px rgba(0, 0, 0, 0.24)',
    '0px 40px 80px rgba(0, 0, 0, 0.25)',
    '0px 42px 84px rgba(0, 0, 0, 0.26)',
    '0px 44px 88px rgba(0, 0, 0, 0.27)',
    '0px 46px 92px rgba(0, 0, 0, 0.28)',
    '0px 48px 96px rgba(0, 0, 0, 0.29)',
  ],
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          maxWidth: 'none !important',
          paddingLeft: '1px !important',
          paddingRight: '250px !important',
          width: '100% !important'
        },
        maxWidthLg: {
          maxWidth: 'none !important'
        },
        maxWidthMd: {
          maxWidth: 'none !important'
        },
        maxWidthSm: {
          maxWidth: 'none !important'
        },
        maxWidthXl: {
          maxWidth: 'none !important'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: '8px 16px',
          boxShadow: 'none',
        },
        contained: {
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
          borderRadius: 12,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: 'rgba(0, 0, 0, 0.8)',
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.12)',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
          },
        },
      },
    },

    //  Force the MUI Menu to have a white background
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff !important',
        },
      },
    },

    //  Force menu items text to be black, including any nested MUI typography
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#000000 !important',
          '& .MuiListItemText-root': {
            color: '#000000 !important',
            '& .MuiTypography-root': {
              color: '#000000 !important',
            },
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 0, 0, 0.08) !important',
            color: '#000000 !important',
            '& .MuiListItemText-root': {
              color: '#000000 !important',
              '& .MuiTypography-root': {
                color: '#000000 !important',
              },
            },
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.12) !important',
              color: '#000000 !important',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
            color: '#000000 !important',
            '& .MuiListItemText-root': {
              color: '#000000 !important',
              '& .MuiTypography-root': {
                color: '#000000 !important',
              },
            },
          },
        },
      },
    },

    // Global scroll lock disable for all components that can cause content shifting
    MuiDialog: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    MuiPopover: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    MuiModal: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    MuiDrawer: {
      defaultProps: {
        disableScrollLock: true,
      },
      styleOverrides: {
        paper: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        MenuProps: {
          disableScrollLock: true,
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        PopperProps: {
          disablePortal: false,
        },
        componentsProps: {
          popper: {
            disablePortal: false,
          },
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    MuiSnackbar: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
  },
});

export default theme;